import { AnimatePresence, motion } from 'framer-motion';
import React, { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import Layout from './components/layout/Layout';
import RouteBoundary from './components/layout/RouteBoundary';
import { CommandPalette } from './components/ui/CommandPalette';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { NavigationCommandSeed } from './components/ui/NavigationCommandSeed';
import { BatchUploadProvider } from './contexts/BatchUploadContext';
import { ChatActivityProvider } from './contexts/ChatActivityContext';
import { TenantProvider } from './contexts/TenantContext';
import { CommandPaletteProvider } from './lib/commandPalette/registry';

// Lazy-load pages for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Connections = React.lazy(() => import('./pages/Connections'));
const Settings = React.lazy(() => import('./pages/Settings'));
const BusinessProfile = React.lazy(() => import('./pages/BusinessProfile'));
const Pipeline = React.lazy(() => import('./pages/Pipeline'));
const Login = React.lazy(() => import('./pages/Login'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const TwoStepSetup = React.lazy(() => import('./pages/TwoStepSetup'));
// XC-TEAM-INVITES Phase 1: the Team page and the three single-use-link pages.
const Team = React.lazy(() => import('./pages/Team'));
const JoinPage = React.lazy(() => import('./pages/JoinPage'));
const GoogleCallback = React.lazy(() => import('./pages/GoogleCallback'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Inbox = React.lazy(() => import('./pages/Inbox'));
const EntityDecisions = React.lazy(() => import('./pages/EntityDecisions'));
const Agents = React.lazy(() => import('./pages/Agents'));
const Rules = React.lazy(() => import('./pages/Rules'));
const WhatIf = React.lazy(() => import('./pages/WhatIf'));
const Documents = React.lazy(() => import('./pages/Documents'));
const ActionHistory = React.lazy(() => import('./pages/ActionHistory'));
const GraphExplorer = React.lazy(() => import('./pages/GraphExplorer'));
const EmployeeDetail = React.lazy(() => import('./pages/EmployeeDetail'));
const MyProfile = React.lazy(() => import('./pages/MyProfile'));
const People = React.lazy(() => import('./pages/People'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const Reports = React.lazy(() => import('./pages/Reports'));
const TrustConsole = React.lazy(() => import('./pages/TrustConsole'));
// Dev-only routes (gated by import.meta.env.DEV below).
const DevReportsTokens = React.lazy(() => import('./pages/dev/ReportsTokens'));

// Page transition animation
const pageVariants = {
  initial: { opacity: 0, y: 16 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -16 },
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.25,
};

// A page that fails to load or render shows a reload offer in its place; the sidebar and header stay
const MotionPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <motion.div className="h-full" initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
    <RouteBoundary>{children}</RouteBoundary>
  </motion.div>
);

function AppRoutes() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen bg-app-gradient">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Routes>
        {/* Public route — login (invite-only, no self-registration) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* First sign-in with an admin-set password (CASA 1.1.2); the page
            guards itself: signed-out -> /login, nothing to change -> / */}
        <Route path="/change-password" element={<ChangePassword />} />

        {/* Required two-step setup (C11); reached only from Login, which hands
            the enrollment-only token over in a module variable
            (components/auth/enrollmentHandoff), never router state or the URL;
            without one it links to sign-in */}
        <Route path="/two-step-setup" element={<TwoStepSetup />} />

        {/* XC-TEAM-INVITES: an invite email's join page and the password-reset pages. Bare routes
            (no PublicRoute): someone signed in to one workspace can still accept an invite to
            another, and each page guards its own link. The link rides the URL FRAGMENT
            (/join#<link>), which never reaches a server or a log. */}
        <Route path="/join" element={<JoinPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* XC-GOOGLE-SIGNIN: where Google sends the person back. Bare, like /join: it takes this
            tab's flow pass once and starts a session (or the picker, the code, a card). */}
        <Route path="/auth/google/callback" element={<GoogleCallback />} />

        {/* OAuth callback — public, handles popup close + postMessage */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />

        {/* Protected routes — full app layout with sidebar + header */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <div className="min-h-screen bg-app-gradient">
                {/* Animated background blobs */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute -inset-10 opacity-10">
                    <div className="absolute top-0 -left-4 w-72 h-72 bg-brand-600 rounded-full mix-blend-multiply filter blur-xl animate-float" />
                    <div className="absolute top-0 -right-4 w-72 h-72 bg-accent-600 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '2s' }} />
                    <div className="absolute -bottom-8 left-20 w-72 h-72 bg-brand-700 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '4s' }} />
                  </div>
                </div>

                <TenantProvider>
                  <BatchUploadProvider>
                    <ChatActivityProvider>
                      <CommandPaletteProvider>
                        <NavigationCommandSeed />
                        <CommandPalette />
                        <Layout>
                          <AnimatePresence mode="wait">
                            <Routes>
                              <Route path="/" element={<MotionPage><Dashboard /></MotionPage>} />
                              <Route path="/connections" element={<MotionPage><Connections /></MotionPage>} />
                              <Route path="/settings" element={<MotionPage><Settings /></MotionPage>} />
                              <Route path="/team" element={<MotionPage><Team /></MotionPage>} />
                              <Route path="/profile" element={<MotionPage><MyProfile /></MotionPage>} />
                              <Route path="/business-profile" element={<MotionPage><BusinessProfile /></MotionPage>} />
                              <Route path="/pipeline" element={<MotionPage><Pipeline /></MotionPage>} />
                              <Route path="/inbox" element={<MotionPage><Inbox /></MotionPage>} />
                              <Route path="/entity-decisions" element={<MotionPage><EntityDecisions /></MotionPage>} />
                              <Route path="/documents" element={<MotionPage><Documents /></MotionPage>} />
                              <Route path="/agents" element={<MotionPage><Agents /></MotionPage>} />
                              <Route path="/rules" element={<MotionPage><Rules /></MotionPage>} />
                              <Route path="/what-if" element={<MotionPage><WhatIf /></MotionPage>} />
                              <Route path="/history" element={<MotionPage><ActionHistory /></MotionPage>} />
                              <Route path="/graph" element={<MotionPage><GraphExplorer /></MotionPage>} />
                              <Route path="/people" element={<MotionPage><People /></MotionPage>} />
                              <Route path="/people/:canonicalEntityId" element={<MotionPage><EmployeeDetail /></MotionPage>} />
                              <Route path="/notifications" element={<MotionPage><Notifications /></MotionPage>} />
                              <Route path="/reports" element={<MotionPage><Reports /></MotionPage>} />
                              <Route path="/trust-console" element={<MotionPage><TrustConsole /></MotionPage>} />
                              {import.meta.env.DEV && (
                                <Route path="/dev/reports-tokens" element={<MotionPage><DevReportsTokens /></MotionPage>} />
                              )}
                              {/* Legacy routes — preserve deep links + OAuth redirect targets during backend deploy gap */}
                              <Route path="/setup" element={<Navigate to="/connections" replace />} />
                              <Route path="/data-sources" element={<Navigate to="/connections" replace />} />
                              <Route path="/review" element={<Navigate to="/inbox" replace />} />
                              <Route path="/pipeline-funnel" element={<Navigate to="/pipeline" replace />} />
                              {/* Redirect unknown routes to Dashboard */}
                              <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                          </AnimatePresence>
                        </Layout>
                      </CommandPaletteProvider>
                    </ChatActivityProvider>
                  </BatchUploadProvider>
                </TenantProvider>
              </div>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

/** The routes inside a boundary of their own too, for the pages outside the app's frame (sign-in, join, reset). */
function App() {
  return (
    <RouteBoundary>
      <AppRoutes />
    </RouteBoundary>
  );
}

export default App;
